/**
 * Storage Accounts Help Content
 * 
 * Feature: 136-dashboard-alignment (T045)
 * Pages: /storage-accounts, /storage-accounts/catalog
 * Constitution: v1.12.0
 */

import type { PageHelpContent } from '../../components/ui/PageHelpPanel';

export const storageAccountsHelp: PageHelpContent = {
  pageTitle: 'Storage Accounts',
  purpose: 'The ARM-discovered Azure storage accounts behind the estate — their replication (durability), access tier (cost), size and backup role — plus where they can be right-sized for cost.',
  sections: [
    {
      title: 'Overview',
      paragraphs: [
        'These are the Azure storage accounts collected into dbo.storage_accounts across every subscription. Each row shows the account\'s replication SKU, access tier, measured used capacity, container count and whether it holds backups. The KPI tiles summarize the loaded set — hover the (i) on each tile for its exact number and source.',
        'Every figure is DB-collected. Nothing is probed live and no number is fabricated — a metric that has not been collected shows as “—”, never a guessed value.',
      ],
    },
    {
      title: 'Replication types (durability vs price)',
      paragraphs: [
        'Replication is how many copies Azure keeps and where. It is the biggest lever on both durability and data-at-rest price. Multipliers below are relative to LRS (LRS = 1.0×); geo options also add inter-region egress.',
      ],
      concepts: [
        { term: 'LRS — Locally redundant (1.0×)', definition: 'Three copies in ONE datacenter. Protects against disk/rack failure only. Cheapest; no protection from a datacenter or regional outage.' },
        { term: 'ZRS — Zone redundant (~1.25×)', definition: 'Three copies across availability zones in one region. Survives a datacenter/zone outage; still single-region.' },
        { term: 'GRS — Geo redundant (~2.0×)', definition: 'LRS plus an asynchronous copy to a paired region. Survives a full regional outage. The secondary is NOT readable until a failover.' },
        { term: 'RA-GRS — Read-access geo (~2.5×)', definition: 'GRS with read access to the secondary region at all times.' },
        { term: 'GZRS — Geo-zone redundant (~2.5×)', definition: 'ZRS in the primary region plus a geo copy — zone AND region resilience.' },
        { term: 'RA-GZRS — Read-access geo-zone (~3.1×)', definition: 'GZRS with read access to the secondary. The most durable and most expensive.' },
      ],
    },
    {
      title: 'Access tiers (cost vs retrieval)',
      concepts: [
        { term: 'Hot', definition: 'Highest storage price, lowest read/transaction cost. For data accessed frequently.' },
        { term: 'Cool', definition: 'Roughly half the Hot storage price, but higher read/retrieval and transaction costs and a 30-day minimum-retention charge. Best for short-term backups and infrequently-read data.' },
        { term: 'Archive', definition: 'Cheapest storage, but data is offline: retrieval (rehydration) takes hours and a 180-day minimum applies. Only for cold data you rarely need back.' },
      ],
    },
    {
      title: 'Cost optimization',
      paragraphs: [
        'The Storage Insights page has a “Cost optimization” section with data-grounded findings — see Related Pages below. Two levers:',
      ],
      concepts: [
        { term: 'Right-size the tier', definition: 'Backup-purpose accounts on Hot are usually written once and read rarely — Cool halves their storage rate. Candidates are heuristic (there is no per-blob last-access telemetry), so move only data you are confident is cold, and mind Cool/Archive retrieval + minimum-retention costs.' },
        { term: 'Right-size replication', definition: 'Non-critical / scratch data on RA-GRS or GRS pays 2–2.5× for geo-redundancy it may not need — dropping to LRS saves ~50–60% but loses regional DR. Conversely, backups on LRS have no geo-DR; GRS adds a paired-region copy at roughly double the rate.' },
      ],
    },
    {
      title: 'Fleet vs Backup',
      concepts: [
        { term: 'Protection = Fleet', definition: 'A working/data account — nothing references it as a backup target.' },
        { term: 'Protection = Backup', definition: 'The account holds backups, classified by BINDING evidence (a backup run, storage map, plan target or copy config references it) — never by the unreliable storage_purpose field.' },
      ],
    },
    {
      title: 'Related Pages',
      links: [
        { label: 'Storage Insights (cost optimization)', href: '/operations/storage-analytics' },
        { label: 'Cost Analytics', href: '/insights/cost' },
        { label: 'Lakehouses', href: '/lakehouses' },
        { label: 'Warehouses', href: '/warehouses' },
      ],
    },
  ],
};

export default storageAccountsHelp;
